// frontend/src/pages/Sabesp/api/sabespClient.js
//
// Cliente do Assistente NTS. Fala com o proprio backend Flask da Iris
// (REACT_APP_API_URL + /sabesp-nts/*), que atua como proxy autenticado para
// a Azure Function real (npd-iris-sabesp-nts) -- a function key da Function
// nunca chega ao navegador, fica só no servidor (SABESP_NTS_FUNCTION_KEY).
// Todas as chamadas exigem o JWT da sessão Iris (Authorization: Bearer
// <accessToken>), por isso cada função recebe o token explicitamente.

const API_BASE = `${process.env.REACT_APP_API_URL}/sabesp-nts`;

export function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseJsonOrThrow(resp, fallbackMessage) {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.detail || `${fallbackMessage} (HTTP ${resp.status})`);
  }
  return data;
}

export async function postChat(query, token, { sessionId = null, groupId = null, signal } = {}) {
  const resp = await fetch(`${API_BASE}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ query, session_id: sessionId, group_id: groupId, filters: {} }),
    signal,
  });
  return parseJsonOrThrow(resp, "Falha na consulta");
}

export async function getCitation(citationId, token) {
  const resp = await fetch(`${API_BASE}/citations/${citationId}`, { headers: authHeader(token) });
  if (!resp.ok) throw new Error("Citação não encontrada");
  return resp.json();
}

export async function getFigure(figureId, token) {
  const resp = await fetch(`${API_BASE}/figures/${figureId}`, { headers: authHeader(token) });
  if (!resp.ok) throw new Error("Figura não encontrada");
  return resp.json();
}

export function documentContentUrl(documentId) {
  return `${API_BASE}/documents/${documentId}/content`;
}

export class HttpError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

// So a pagina citada (e as vizinhas) como um PDF pequeno, em vez do documento inteiro.
// `start` e a primeira pagina do original contida no recorte; `count` e o total de paginas do documento.
export async function fetchDocumentPage(documentId, page, token, window = 2) {
  const resp = await fetch(`${API_BASE}/documents/${documentId}/page?n=${page}&window=${window}`, {
    headers: authHeader(token),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new HttpError(data.detail || `Falha ao abrir a página (HTTP ${resp.status})`, resp.status);
  }
  const buffer = await resp.arrayBuffer();
  return {
    data: new Uint8Array(buffer),
    start: Number(resp.headers.get("X-Page-Start")) || Math.max(1, page - window),
    count: Number(resp.headers.get("X-Page-Count")) || null,
  };
}

export function figureThumbnailUrl(figureId) {
  return `${API_BASE}/figures/${figureId}/thumbnail`;
}

export async function listGroups(token) {
  const resp = await fetch(`${API_BASE}/groups`, { headers: authHeader(token) });
  const data = await parseJsonOrThrow(resp, "Falha ao listar grupos");
  return data.items || [];
}

export async function createGroup(name, token) {
  const resp = await fetch(`${API_BASE}/groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ name }),
  });
  return parseJsonOrThrow(resp, "Falha ao criar grupo");
}

export async function renameGroup(groupId, name, token) {
  const resp = await fetch(`${API_BASE}/groups/${groupId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ name }),
  });
  return parseJsonOrThrow(resp, "Falha ao renomear grupo");
}

export async function deleteGroup(groupId, token) {
  const resp = await fetch(`${API_BASE}/groups/${groupId}`, {
    method: "DELETE",
    headers: authHeader(token),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || "Falha ao excluir grupo");
  }
}

export async function listGroupDocuments(groupId, token) {
  const resp = await fetch(`${API_BASE}/groups/${groupId}/documents`, { headers: authHeader(token) });
  const data = await parseJsonOrThrow(resp, "Falha ao listar arquivos");
  return data.items || [];
}

export async function uploadGroupDocument(groupId, file, token) {
  const fd = new FormData();
  fd.append("file", file);
  const resp = await fetch(`${API_BASE}/groups/${groupId}/documents`, {
    method: "POST",
    headers: authHeader(token),
    body: fd,
  });
  return parseJsonOrThrow(resp, "Falha ao enviar arquivo");
}

export async function deleteGroupDocument(groupId, documentId, token) {
  const resp = await fetch(`${API_BASE}/groups/${groupId}/documents/${documentId}`, {
    method: "DELETE",
    headers: authHeader(token),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || "Falha ao excluir arquivo");
  }
}

const IRIS_API_BASE = process.env.REACT_APP_API_URL;

export async function listSessions(assistantId, email, token) {
  const resp = await fetch(`${IRIS_API_BASE}/post_list_session`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ assistant_id: assistantId, email }),
  });
  const data = await parseJsonOrThrow(resp, "Falha ao listar sessões");
  return data.id_list || [];
}

export async function getSessionHistory(assistantId, sessionId, token) {
  const resp = await fetch(`${IRIS_API_BASE}/post_session`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ assistant_id: assistantId, session_id: sessionId }),
  });
  const data = await parseJsonOrThrow(resp, "Falha ao carregar histórico");
  return (data.history || []).filter((m) => m.role === "user" || m.role === "assistant");
}

export async function postFeedback(interactionId, feedback, token) {
  const resp = await fetch(`${IRIS_API_BASE}/post_feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ interaction_id: interactionId, feedback }),
  });
  return parseJsonOrThrow(resp, "Falha ao enviar feedback");
}

export async function postSummaryChat(history, interactionId, sessionId, token) {
  const resp = await fetch(`${IRIS_API_BASE}/post_summary_chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ history, interaction_id: interactionId, session_id: sessionId }),
  });
  return parseJsonOrThrow(resp, "Falha ao gerar título da conversa");
}
